/**
 * Implementação do repositório de Reminder: orquestra datasource remoto e local.
 */

import type { Reminder } from '@/core/domain';
import type { ReminderRepository } from '../../domain/repositories/reminder-repository';
import type { ReminderRemoteDataSource } from '../datasources/remote';
import type { ReminderLocalDataSource } from '../datasources/local';

const LOG_PREFIX = '[Reminders][Repository]';

function repoDebug(...args: unknown[]): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug(LOG_PREFIX, ...args);
  }
}

export class ReminderRepositoryImpl implements ReminderRepository {
  constructor(
    private readonly remote: ReminderRemoteDataSource,
    private readonly local: ReminderLocalDataSource
  ) {}

  async getById(id: string): Promise<Reminder | null> {
    const fromCache = await this.local.getById(id);
    if (fromCache != null) return fromCache;
    const fromRemote = await this.remote.getById(id);
    if (fromRemote != null) await this.local.create(fromRemote).catch(() => {});
    return fromRemote;
  }

  async getAllByUserId(userId: string): Promise<Reminder[]> {
    return this.getAll(userId, false);
  }

  async getAll(userId: string, forceRefresh = false): Promise<Reminder[]> {
    repoDebug('getAll', { userId, forceRefresh });
    if (!forceRefresh) {
      const fromCache = await this.local.getAll(userId);
      if (fromCache.length > 0) {
        repoDebug('getAll: returning cache', { userId, count: fromCache.length });
        return fromCache;
      }
      repoDebug('getAll: cache empty, fetching remote', { userId });
    } else {
      repoDebug('getAll: forceRefresh, skipping cache', { userId });
    }
    const fromRemote = await this.remote.getAll(userId);
    repoDebug('getAll: remote returned', { userId, count: fromRemote.length });
    await this.local.cacheAll(userId, fromRemote).catch(() => {});
    return fromRemote;
  }

  async create(reminder: Reminder): Promise<Reminder> {
    const created = await this.remote.create(reminder);
    await this.local.create(created).catch(() => {});
    return created;
  }

  async update(id: string, reminder: Partial<Reminder>): Promise<Reminder> {
    const updated = await this.remote.update(id, reminder);
    await this.local.update(id, updated).catch(() => {});
    return updated;
  }

  async delete(id: string): Promise<void> {
    await this.remote.delete(id);
    await this.local.delete(id).catch(() => {});
  }
}
